import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import type { Quotation } from '@/types/quotation';
import { formatCurrency, cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQuotationStore } from '@/stores/quotationStore';
import { toast } from 'sonner';
import {
  FileText,
  Search,
  Plus,
  Copy,
  Calendar,
  LayoutDashboard,
  Trash2,
  Users,
  Loader2,
  Ban,
  FileEdit,
  BookTemplate,
  Wand2,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SaveTemplateDialog } from './SaveTemplateDialog';

/** Number of cards to render beyond the visible viewport (virtual window buffer) */
const VIRTUAL_PAGE_SIZE = 30;

interface QuotationListProps {
  quotations: Quotation[];
  onSelect: (quotation: Quotation) => void;
  onClone: (quoteId: string) => void;
  onCreateNew: () => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
}

export function QuotationList({
  quotations,
  onSelect,
  onClone,
  onCreateNew,
  searchQuery: externalSearchQuery,
  onSearchChange,
  isLoading = false,
}: QuotationListProps) {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const searchQuery = externalSearchQuery !== undefined ? externalSearchQuery : internalSearchQuery;
  const [activeTab, setActiveTab] = useState<'all' | 'drafts' | 'finalized' | 'templates'>('all');
  const { 
    deleteQuotation, 
    updateMetadata, 
    saveQuotation, 
    fetchQuotations, 
    fetchQuotationById,
    toggleTemplateStatus,
    createRevision,
  } = useQuotationStore();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  
  const [cancelQuote, setCancelQuote] = useState<Quotation | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCancellingList, setIsCancellingList] = useState(false);

  const [revisionQuote, setRevisionQuote] = useState<Quotation | null>(null);
  const [isRevisingList, setIsRevisingList] = useState(false);
  const [templateDialogQuote, setTemplateDialogQuote] = useState<Quotation | null>(null);
  // Virtual window: only render the first N cards; expand on demand
  const [visibleCount, setVisibleCount] = useState(VIRTUAL_PAGE_SIZE);

  const filteredQuotations = useMemo(() => (quotations || []).filter((q) => {
    try {
      if (!q || !q.metadata) return false;

      // Tab filter
      if (activeTab === 'drafts' && q.metadata.status !== 'draft') return false;
      if (activeTab === 'finalized' && q.metadata.status !== 'finalized') return false;
      if (activeTab === 'templates' && !q.metadata.isTemplate) return false;

      const search = searchQuery.toLowerCase();
      if (!search) return true;
      return (
        (q.metadata.quoteNumber || '').toLowerCase().includes(search) ||
        (q.client?.name || '').toLowerCase().includes(search) ||
        (q.client?.projectName || '').toLowerCase().includes(search) ||
        (q.metadata.templateName || '').toLowerCase().includes(search)
      );
    } catch (e) {
      console.error('Record skipped due to error:', q, e);
      return false;
    }
  }), [quotations, searchQuery, activeTab]);

  // Reset visible window when search changes
  const handleSearch = (val: string) => {
    setInternalSearchQuery(val);
    setVisibleCount(VIRTUAL_PAGE_SIZE);
    if (onSearchChange) onSearchChange(val);
  };

  const visibleQuotations = filteredQuotations.slice(0, visibleCount);
  const hasMore = visibleCount < filteredQuotations.length;

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteQuotation(deleteId);
      toast.success('Estimate deleted successfully');
    } catch (error) {
      toast.error('Failed to delete estimate');
    } finally {
      setDeleteId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelQuote || cancelReason.trim().length < 5) return;
    setIsCancellingList(true);
    try {
      const { cancelQuotation } = useQuotationStore.getState();
      await cancelQuotation(cancelQuote.metadata.quoteId!, cancelReason);
      toast.success(`Quotation ${cancelQuote.metadata.quoteNumber} cancelled successfully.`);
    } catch (err: unknown) {
      console.error('[QuotationList] Cancel error:', err);
      toast.error('Failed to cancel quotation: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsCancellingList(false);
      setCancelQuote(null);
      setCancelReason('');
    }
  };

  const handleCreateRevision = async () => {
    if (!revisionQuote) return;
    setIsRevisingList(true);
    try {
      const parentId = revisionQuote.metadata.quoteId!;
      const revisionFn = createRevision || useQuotationStore.getState().createRevision || ((id: string) => quotationService.createRevision(id));
      const newId = await revisionFn(parentId);
      if (newId) {
        toast.success('New revision drafted. Preparing workspace...');
        await fetchQuotationById(newId);
        const updated = useQuotationStore.getState().currentQuotation;
        if (updated) onSelect(updated);
      }
    } catch (err: unknown) {
      console.error('[QuotationList] Revision error:', err);
      toast.error('Failed to create revision: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsRevisingList(false);
      setRevisionQuote(null);
    }
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const variants: Record<string, string> = {
      draft: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      revised: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      finalized: 'bg-emerald-600 text-white border-transparent shadow-lg shadow-emerald-600/20',
      pending_approval: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      rejected: 'bg-rose-500/10 text-rose-600 border-rose-500/20',
      superseded: 'bg-zinc-500/10 text-zinc-600 border-zinc-500/20',
      cancelled: 'bg-rose-900/10 text-rose-800 border-rose-900/20',
      archived: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
    };

    return (
      <Badge
        className={cn(
          "text-[9px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-lg border",
          variants[status] || 'bg-slate-100 text-slate-600 border-slate-200'
        )}
      >
        {status}
      </Badge>
    );
  };

  return (
    <div className="animate-fade-in space-y-6 pb-20">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="acc-panel p-3 sm:p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4 overflow-hidden"
      >
        <div className="relative z-10 space-y-1">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="acc-micro-label">Quotation Registry</span>
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-foreground tracking-tighter uppercase font-display">
            Estimate <span className="text-emerald-500">Repository</span>
          </h2>
          <p className="text-[9px] sm:text-[10px] font-medium text-muted-foreground uppercase tracking-widest max-w-md">
            Centralized quotation workspace for current and historical estimates.
          </p>
        </div>
        <Button 
          onClick={onCreateNew} 
          data-tour="new-quote-button"
          className="h-9 sm:h-10 px-4 sm:px-6 rounded-xl font-semibold uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 active:scale-95 transition-all text-white border-none text-[9px] sm:text-[10px] relative z-10"
        >
          <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1.5 sm:mr-2" /> New Estimate
        </Button>
      </motion.div>

      <div className="relative group max-w-2xl mx-auto mb-4" data-tour="search-registry">
        <div className="relative flex items-center bg-muted/50 border border-border rounded-xl px-3 sm:px-5 h-10 focus-within:border-emerald-500/30 transition-all">
          <Search className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-emerald-500/60 mr-2 sm:mr-3" />
          <input
            placeholder="SEARCH BY QUOTE ID, CLIENT, OR PROJECT..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="bg-transparent border-0 outline-none text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground placeholder:text-slate-800 w-full"
          />
          {filteredQuotations.length > 0 && (
            <div className="flex items-center gap-2 px-2 py-1 bg-muted/50 border border-border rounded-lg">
                 <span className="text-emerald-500 text-[6px] sm:text-[7px] font-semibold uppercase tracking-widest whitespace-nowrap">
                    {filteredQuotations.length} Results
                </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8">
        {[
          { id: 'all', label: 'All Estimates' },
          { id: 'drafts', label: 'Drafts' },
          { id: 'finalized', label: 'Finalized' },
          { id: 'templates', label: 'Templates 🌟', isTemplatePill: true },
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setActiveTab(tab.id as any); setVisibleCount(VIRTUAL_PAGE_SIZE); }}
            className={`h-8 rounded-full text-xs font-semibold px-4 transition-all ${
              activeTab === tab.id
                ? tab.isTemplatePill
                  ? 'bg-amber-500 text-slate-950 hover:bg-amber-600 font-bold shadow-md shadow-amber-500/20'
                  : 'bg-emerald-600 text-white hover:bg-emerald-500'
                : tab.isTemplatePill
                ? 'text-amber-500 hover:bg-amber-500/10'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            {tab.isTemplatePill && <BookTemplate className="h-3.5 w-3.5 mr-1.5" />}
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Quotation List — Cardless on desktop, Contained on mobile */}
      <motion.div 
        layout
        initial="hidden"
        animate="show"
        variants={{
          hidden: { opacity: 0 },
          show: {
            opacity: 1,
            transition: { staggerChildren: 0.05 }
          }
        }}
        className="flex flex-col gap-4 md:gap-0 md:border md:border-border md:rounded-2xl md:bg-card/50 md:backdrop-blur-sm overflow-hidden"
      >
        {/* Table Header (Desktop Only) */}
        {visibleQuotations.length > 0 && !isLoading && (
          <div className="hidden md:flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
            <div className="w-1/3 flex items-center gap-6">
              <div className="w-24 shrink-0">
                <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Status</span>
              </div>
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Project Details</span>
            </div>
            <div className="w-1/4 flex items-center justify-center">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Client & Date</span>
            </div>
            <div className="w-1/4 flex items-center justify-end pr-14">
              <span className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest">Valuation</span>
            </div>
          </div>
        )}
        <AnimatePresence mode="popLayout">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <motion.div 
                key={`skeleton-${i}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="zenith-glass h-24 animate-pulse rounded-xl md:rounded-none md:border-b md:last:border-b-0" 
              />
            ))
          ) : visibleQuotations.length === 0 ? (
            <motion.div 
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="h-[200px] flex flex-col items-center justify-center bg-muted/20 rounded-[24px] border border-dashed border-border backdrop-blur-sm m-4"
            >
              <div className="h-12 w-12 rounded-xl bg-muted/50 border border-border flex items-center justify-center mb-3">
                  <LayoutDashboard className="h-5 w-5 text-slate-800" />
              </div>
              <p className="text-slate-700 font-semibold uppercase tracking-[0.4em] text-[9px]">No Active Operational Data Found</p>
              <Button variant="link" onClick={onCreateNew} className="mt-2 text-emerald-500 font-semibold uppercase tracking-widest text-[8px] hover:text-emerald-400">Initiate First Project Node</Button>
            </motion.div>
          ) : (
            visibleQuotations.map((q) => (
              <motion.div
                key={q.metadata.quoteId}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="group cursor-pointer"
                onClick={() => onSelect(q)}
              >
                <div className="
                  bg-card border border-border rounded-xl shadow-black/5 p-4
                  md:bg-transparent md:dark:bg-transparent md:border-transparent md:shadow-none md:rounded-none md:p-4
                  md:flex md:items-center md:justify-between md:border-b md:border-zinc-100 md:dark:border-white/[0.04] md:last:border-b-0
                  hover:bg-muted/50 transition-colors
                ">
                  {/* Left Side: Title & Status */}
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 md:w-1/3">
                    <div className="flex justify-between items-center md:hidden mb-2">
                      <StatusBadge status={q.metadata.status} />
                    </div>
                    <div className="hidden md:block w-24 shrink-0">
                      <StatusBadge status={q.metadata.status} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight line-clamp-1 group-hover:text-emerald-500 transition-colors">
                        {q.title || q.client?.projectName || 'UNTITLED PROJECT'}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-semibold text-emerald-500 uppercase tracking-widest">{q.metadata.quoteNumber}</span>
                        <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest">System Node</span>
                      </div>
                    </div>
                  </div>

                  {/* Middle: Client & Date */}
                  <div className="mt-4 md:mt-0 pt-4 md:pt-0 border-t border-zinc-100 dark:border-white/5 md:border-t-0 flex md:flex-col justify-between md:justify-center gap-2 md:w-1/4">
                    <div className="flex items-center gap-2">
                      <Users className="h-3 w-3 text-zinc-400" />
                      <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 truncate">
                        {q.client_name || q.client?.name || 'Unknown Client'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-500">
                      <Calendar className="h-3 w-3" />
                      <span className="text-[10px] font-medium uppercase tracking-widest">
                        {q.metadata.issuedOn || 'REGISTRY'}
                      </span>
                    </div>
                  </div>

                  {/* Right: Valuation & Actions */}
                  <div className="mt-4 md:mt-0 flex items-center justify-between md:justify-end gap-6 md:w-1/4">
                    <div className="text-left md:text-right">
                      <p className="text-[10px] font-medium text-zinc-500 uppercase tracking-widest mb-0.5">Valuation</p>
                      <p className="text-sm font-semibold tabular-nums tracking-tight text-zinc-900 dark:text-zinc-100 group-hover:text-emerald-500 transition-colors">
                        {formatCurrency(q.financials?.grandTotal || 0)}
                      </p>
                    </div>
                    <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      {q.metadata.status === 'finalized' && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); setRevisionQuote(q); }} 
                            className="h-8 w-8 text-zinc-500 hover:text-emerald-500 hover:bg-emerald-500/10 rounded-md transition-colors"
                            title="Create Revision"
                          >
                            <FileEdit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); setCancelQuote(q); }} 
                            className="h-8 w-8 text-zinc-500 hover:text-rose-500 hover:bg-rose-500/10 rounded-md transition-colors"
                            title="Cancel Estimate"
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={(e) => {
                          e.stopPropagation();
                          setTemplateDialogQuote(q);
                        }} 
                        className={`h-8 w-8 rounded-md transition-colors ${
                          q.metadata.isTemplate
                            ? 'text-amber-500 hover:text-amber-600 hover:bg-amber-500/20 bg-amber-500/10'
                            : 'text-zinc-500 hover:text-amber-500 hover:bg-amber-500/10'
                        }`}
                        title={q.metadata.isTemplate ? `Template: ${q.metadata.templateName || 'Saved'}` : 'Mark as Template'}
                      >
                        <BookTemplate className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onClone(q.metadata.quoteId!); }} className="h-8 w-8 text-zinc-500 hover:text-emerald-500 hover:bg-emerald-500/10 rounded-md transition-colors" title="Duplicate as Draft">
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(q.metadata.quoteId!); }} className="h-8 w-8 text-zinc-500 hover:text-rose-500 hover:bg-rose-500/10 rounded-md transition-colors" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </motion.div>

      {/* Load more button — shown when list is truncated */}
      {hasMore && !isLoading && (
        <div className="flex justify-center pt-3">
          <Button
            variant="outline"
            onClick={() => setVisibleCount(c => c + VIRTUAL_PAGE_SIZE)}
            className="rounded-xl font-semibold uppercase tracking-widest text-[9px] sm:text-[10px] border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 px-6 sm:px-8 h-10 sm:h-12"
          >
            Load more ({filteredQuotations.length - visibleCount} remaining)
          </Button>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => {
        if (!open) {
          setDeleteId(null);
          setDeleteConfirmationText('');
        }
      }}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold uppercase tracking-tight">Delete Estimate</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4" asChild>
              <div className="text-sm text-slate-600 space-y-4 font-medium">
                {(() => {
                  const quoteToDelete = quotations.find(q => q.metadata.quoteId === deleteId);
                  if (!quoteToDelete) return null;

                  return (
                    <>
                      <div className="bg-rose-50 p-4 rounded-xl border border-rose-100 text-rose-700">
                        Permanently delete <strong>{quoteToDelete.metadata.quoteNumber}</strong>? This cannot be undone.
                      </div>
                      <div className="space-y-2 pt-2">
                        <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block">
                          Type <span className="text-emerald-600 select-all">{quoteToDelete.metadata.quoteNumber}</span> to confirm
                        </label>
                        <Input
                          value={deleteConfirmationText}
                          onChange={(e) => setDeleteConfirmationText(e.target.value)}
                          placeholder={quoteToDelete.metadata.quoteNumber}
                          className="font-semibold h-12 rounded-xl border-slate-200 focus-visible:ring-emerald-500"
                          autoFocus
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="p-2">
            <AlertDialogCancel className="rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={(() => {
                const quoteToDelete = quotations.find(q => q.metadata.quoteId === deleteId);
                return !quoteToDelete || deleteConfirmationText !== quoteToDelete.metadata.quoteNumber;
              })()}
              className="bg-rose-600 text-foreground rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12 px-8 shadow-xl shadow-rose-600/20 hover:bg-rose-700 hover:shadow-2xl hover:shadow-rose-600/30 hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-xl"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!cancelQuote} onOpenChange={(open) => {
        if (!open) {
          setCancelQuote(null);
          setCancelReason('');
        }
      }}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold uppercase tracking-tight">Cancel Estimate</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4" asChild>
              <div className="text-sm text-slate-600 space-y-4 font-medium">
                {cancelQuote && (
                  <>
                    <div className="bg-rose-50 p-4 rounded-xl border border-rose-100 text-rose-700">
                      Cancelling <strong>{cancelQuote.metadata.quoteNumber}</strong> will permanently freeze its commercial fields. It can still be duplicated, but not finalized again.
                    </div>
                    <div className="space-y-2 pt-2">
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest block">
                        Reason for Cancellation
                      </label>
                      <Input
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="e.g. Client withdrew, Project deferred"
                        className="font-semibold h-12 rounded-xl border-slate-200 focus-visible:ring-rose-500"
                        autoFocus
                      />
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="p-2">
            <AlertDialogCancel className="rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12">Keep Active</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={!cancelQuote || cancelReason.trim().length < 5 || isCancellingList}
              className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12 px-8 shadow-xl shadow-rose-600/20 transition-all disabled:opacity-50"
            >
              {isCancellingList ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm Cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!revisionQuote} onOpenChange={(open) => {
        if (!open) {
          setRevisionQuote(null);
        }
      }}>
        <AlertDialogContent className="rounded-[24px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-semibold uppercase tracking-tight">Create Revision</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4" asChild>
              <div className="text-sm text-slate-600 space-y-4 font-medium">
                {revisionQuote && (
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 text-emerald-700">
                    This will create a new draft version of <strong>{revisionQuote.metadata.quoteNumber}</strong> while preserving the finalized copy. The new draft will automatically supersede this version upon finalization.
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="p-2">
            <AlertDialogCancel className="rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateRevision}
              disabled={!revisionQuote || isRevisingList}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold uppercase tracking-widest text-[10px] h-12 px-8 shadow-xl shadow-emerald-600/20 transition-all disabled:opacity-50"
            >
              {isRevisingList ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Create Draft Revision
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SaveTemplateDialog
        open={!!templateDialogQuote}
        onOpenChange={(open) => { if (!open) setTemplateDialogQuote(null); }}
        quotation={templateDialogQuote}
      />
    </div>
  );
}
